import { IconButton, TextField, Typography } from "@mui/material";
import { Box, Button, ButtonBase } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import React, { forwardRef, useContext, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { APP_BACKGROUND_COLOR, FONT_FAMILY, TEXT_COLOR } from "../../theme";
import { CreateFolderModalProps } from "./types";
import { addTrackFileModalStyles } from "./styles";
import { createFolderStyles, sendInviteModalStyles } from "./styles";
import { Collaborator, FileState, Folder, User } from "../../proto/api_pb";
import { UsernameSelectField } from "../../pages/MyTracks/styles";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { useStyles } from "../ExploreBase/styles";
import { AuthContext, UserDetailsContext } from "../../contexts";
import { AuthContextInterface } from "../../contexts";
import { UserDetailsContextInterface } from "../../contexts";
import { toast } from "react-toastify";
import { postFolder } from "../../services/folderService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { RpcError } from "grpc-web";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  setIsCreateFolderModalOpen,
  isCreateFolderModalOpen,
  parentFolderId,
  getAllFoldersList,
}) => {
  const [folderName, setFolderName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [usersList, setUsersList] = useState<User[]>([]);
  const [collaboratorValue, setCollaboratorValue] = useState("");
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [addedCollaboratorsIds, setAddedCollaboratorsIds] = useState<string[]>(
    []
  );
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { getValidAccessToken } = useGetValidAccessToken();
  const classes = useStyles();
  const { allUsers, userName } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);

  const addCollaborator = (newCollaborator: string) => {
    if (newCollaborator) {
      if (
        collaborators.find(
          (collaborator) => collaborator.getUsername() === newCollaborator
        ) === undefined &&
        usersList.some((user) => user.getUsername() === newCollaborator)
      ) {
        const addedCollaborator = usersList.find(
          (user) => user.getUsername() === newCollaborator
        );
        if (addedCollaborator) {
          const newCollaborator = new Collaborator();
          newCollaborator.setUserId(addedCollaborator?.getUserId());
          newCollaborator.setUsername(addedCollaborator?.getUsername());
          const updatedCollaborators = [...collaborators, newCollaborator];

          setAddedCollaboratorsIds([
            ...addedCollaboratorsIds,
            addedCollaborator.getUserId(),
          ]);
          setCollaborators(updatedCollaborators);
        }
        const newUserList = usersList.filter(
          (user) => user.getUsername() !== newCollaborator
        );
        setUsersList([...newUserList]);
        setCollaboratorValue("");
      } else {
        toast.info("Username does not exist");
      }
      return true;
    }
  };

  const removeCollaborator = (collaborator: Collaborator) => {
    const updatedCollaborators = collaborators.filter(
      (item) => item !== collaborator
    );
    setCollaborators(updatedCollaborators);

    // updating userIds list that is being passed in sendInvite
    const updatedUserIdList = addedCollaboratorsIds.filter(
      (item) => item !== collaborator.getUserId()
    );
    setAddedCollaboratorsIds([...updatedUserIdList]);
    const newUser = new User();
    newUser.setUsername(collaborator.getUsername());
    newUser.setUserId(collaborator.getUserId());
    const newUsersList = [...usersList, newUser];
    setUsersList([...newUsersList]);
  };

  useEffect(() => {
    const userL = allUsers.filter((user) => user.getUsername() !== userName);
    setUsersList(userL);
  }, [allUsers, userName]);

  const handleCreateFolder = async () => {
    const token = await getValidAccessToken();
    const folder = new Folder();
    folder.setCollaboratorsList(collaborators);
    folder.setDescription(description);
    folder.setFolderState(FileState.FILE_STATE_ACTIVE);
    folder.setName(folderName);
    folder.setOwnerId(userId);
    if (parentFolderId) {
      folder.setParentFolderId(parentFolderId);
    }
    const response = await postFolder(token, folder);
    if (response instanceof RpcError) {
      setIsCreateFolderModalOpen(false);
      toast.error("Fail to create folder");
    } else {
      setIsCreateFolderModalOpen(false);
      getAllFoldersList();
      toast.info(`${folderName} folder created`);
    }
  };

  return (
    <>
      <Dialog
        open={isCreateFolderModalOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setIsCreateFolderModalOpen(false)}
        aria-describedby="alert-dialog-slide-description"
      >
        <Box sx={createFolderStyles.container}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <ButtonBase onClick={() => setIsCreateFolderModalOpen(false)}>
              <CloseIcon />
            </ButtonBase>
          </Box>
          <DialogTitle>Create Folder</DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: "15px" }}
          >
            <Box>
              <Typography sx={{ marginBottom: "8px" }}>Name</Typography>
              <TextField
                fullWidth
                id="outlined-basic"
                label="Folder Name"
                variant="outlined"
                onChange={(e) => setFolderName(e.target.value)}
              />
            </Box>
            <Box>
              <Typography sx={{ marginBottom: "8px" }}>Description</Typography>
              <TextField
                fullWidth
                id="outlined-basic"
                label="Description"
                variant="outlined"
                onChange={(e) => setDescription(e.target.value)}
              />
            </Box>
            <Box>
              <Box>
                <span>Add Collaborators </span>
              </Box>
              <hr />
              <Box sx={addTrackFileModalStyles.collaboratorContainer}>
                <Box sx={{ width: "80%" }}>
                  <UsernameSelectField
                    freeSolo
                    forcePopupIcon={false}
                    isOptionEqualToValue={(option, value) => option === value}
                    inputValue={collaboratorValue}
                    onInputChange={(
                      event: React.SyntheticEvent<Element, Event>,
                      value: string
                    ) => {
                      setCollaboratorValue(value);
                    }}
                    options={usersList.map((item) => item.getUsername()).sort()}
                    renderInput={(params) => (
                      <TextField
                        style={{
                          backgroundColor: APP_BACKGROUND_COLOR,
                          color: TEXT_COLOR,
                        }}
                        {...params}
                        label=" "
                        InputLabelProps={{ shrink: false }}
                        classes={{ root: classes.customTextField }}
                      />
                    )}
                    renderOption={(props, option: any) => (
                      <li {...props} style={{ fontFamily: FONT_FAMILY }}>
                        {option}
                      </li>
                    )}
                  />
                </Box>
                <Button
                  disabled={collaboratorValue === ""}
                  sx={addTrackFileModalStyles.collaboratorAddButton}
                  size="small"
                  variant="contained"
                  onClick={() => {
                    addCollaborator(collaboratorValue);
                  }}
                >
                  Add
                </Button>
              </Box>
              <Box className="collaborators" style={{ margin: "20px 0 40px" }}>
                <Box className="collaborators-target">
                  {collaborators &&
                    collaborators.length > 0 &&
                    collaborators.map((collaborator, index) => (
                      <Box className="collaborator" key={index}>
                        <p style={{ fontFamily: FONT_FAMILY, flexGrow: 1 }}>
                          {collaborator.getUsername()}
                        </p>
                        <IconButton
                          onClick={() => removeCollaborator(collaborator)}
                        >
                          <DeleteForeverIcon />
                        </IconButton>
                      </Box>
                    ))}
                </Box>
              </Box>
            </Box>
            <Button
              onClick={handleCreateFolder}
              sx={{ ...sendInviteModalStyles.button, ...{ height: "auto" } }}
            >
              Create Folder
            </Button>
          </DialogContent>
        </Box>
      </Dialog>
    </>
  );
};
